import type { ComponentProps, ReactNode } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { ScrollStrip } from './ScrollStrip';

/**
 * Underline tabs — the Sonar pipeline-tabs anatomy (105) on the vendored shadcn/Radix
 * tabs primitives, which own the a11y contract: roving tabindex, arrow keys that move
 * selection and focus together, aria wiring, focus ring. Design-language §3's "~3 tabs"
 * cap is amended by A-2: the strip must FIT 390px by scrolling horizontally
 * (`overflow-x-auto` + `min-w-max`), never by wrapping — density is now sanctioned,
 * width overflow still is not.
 *
 * Active color stays ServWell's brand (`border-brand`, `--sw-brand-text`) — the effort
 * adopts Sonar's anatomy, not its palette. Controlled-only, like the component it
 * replaces: every page owns where its tab state lives (URL or local).
 */
export function UnderlineTabs({
  value,
  onValueChange,
  className,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  children: ReactNode;
}) {
  // gap-6 over the vendored gap-2: panels sit a full page-rhythm step (24px) below the
  // strip, matching the space-y-6 flow of PageRoot the strip used to sit in directly.
  return (
    <Tabs value={value} onValueChange={onValueChange} className={cn('gap-6', className)}>
      {children}
    </Tabs>
  );
}

/**
 * The strip. `flush` drops the container's own hairline for in-panel use — the Sheet's
 * header already draws a full-width `border-b` directly above the tabs slot, and two
 * gray rules 13px apart was 104's m5 finding. The brand underline on the active tab is
 * an indicator, not a rule, so it stays in both variants.
 *
 * ⭐ It scrolls through `ScrollStrip`, so a strip that overflows SAYS so: a fade and a
 * chevron at each end with more tabs past it (UX baseline). Arrow keys already bring the
 * focused tab into view.
 */
export function UnderlineTabList({
  children,
  flush,
}: {
  children: ReactNode;
  flush?: boolean;
}) {
  return (
    <ScrollStrip bordered={!flush}>
      <TabsList className="h-auto min-w-max gap-6 bg-transparent p-0">
        {children}
      </TabsList>
    </ScrollStrip>
  );
}

/**
 * ⚠️ The `dark:` repeats are NOT redundant: the vendored trigger styles its active state
 * as a filled pill under `dark:` (`dark:data-[state=active]:bg-input/30`,
 * `…border-input`, `…text-foreground`), and tailwind-merge only drops a vendored class
 * when ours carries the SAME variant stack. Delete the dark triplet and dark mode
 * silently gets the pill back while light keeps the underline.
 */
const TRIGGER_CLASS =
  'rounded-none border-0 border-b-2 border-transparent bg-transparent px-0 pb-3 text-sm font-medium text-muted-foreground shadow-none hover:text-text data-[state=active]:border-0 data-[state=active]:border-b-2 data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-[var(--sw-brand-text)] data-[state=active]:shadow-none dark:data-[state=active]:border-0 dark:data-[state=active]:border-b-2 dark:data-[state=active]:border-brand dark:data-[state=active]:bg-transparent dark:data-[state=active]:text-[var(--sw-brand-text)]';

/** One tab. No className passthrough by design — every strip in the app looks the same. */
export function UnderlineTab({
  value,
  children,
  ...props
}: { value: string; children: ReactNode } & Omit<
  ComponentProps<typeof TabsTrigger>,
  'value' | 'className' | 'children'
>) {
  return (
    <TabsTrigger value={value} className={TRIGGER_CLASS} {...props}>
      {children}
    </TabsTrigger>
  );
}

/**
 * Every strip is the same five lines of triggers over a `{key,label}` list — this is
 * those five lines, once. Compose UnderlineTabList/UnderlineTab directly only when a
 * trigger needs more than a label.
 */
export function UnderlineTabStrip({
  tabs,
  flush,
}: {
  tabs: readonly { key: string; label: string }[];
  flush?: boolean;
}) {
  return (
    <UnderlineTabList flush={flush}>
      {tabs.map((t) => (
        <UnderlineTab key={t.key} value={t.key}>
          {t.label}
        </UnderlineTab>
      ))}
    </UnderlineTabList>
  );
}

/**
 * The panel, on Radix TabsContent.
 *
 * ⚠️ `keepMounted` is a DATA-SAFETY switch, not a perf knob. Radix unmounts an
 * inactive panel, and unmounting throws away everything a card inside was holding —
 * a half-typed note, an add-contact form, a parts row (review 105 M1: the flat pages
 * these panels replaced could never lose a draft to a click on a heading). A panel
 * whose cards carry ANY form state must pass `keepMounted`: it stays mounted and
 * merely hides, exactly the pre-tabs mount behavior. Leave it off only for read-only
 * or self-persisting content, where lazy mounting is a free win.
 *
 * The panel is a tab stop (Radix gives it tabIndex=0 per the APG, so a keyboard user
 * can land in the content) — the vendored `outline-none` would make that landing
 * invisible, so the ring here puts it back.
 */
export function UnderlineTabPanel({
  keepMounted,
  className,
  children,
  ...props
}: { keepMounted?: boolean } & ComponentProps<typeof TabsContent>) {
  return (
    <TabsContent
      {...(keepMounted ? { forceMount: true as const } : {})}
      className={cn(
        'focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=inactive]:hidden',
        className,
      )}
      {...props}
    >
      {children}
    </TabsContent>
  );
}
